import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Text,
  Button,
  Card,
  TypographyStylesProvider,
} from '@mantine/core'
import { IconArrowLeft } from '@tabler/icons-react'
import type { ContentEntry } from '../../content'

interface MarkdownPageProps {
  getBySlug: (slug: string) => ContentEntry | undefined
  backPath: string
  backLabel: string
}

/**
 * Simple markdown-to-HTML converter.
 * Handles headings, bold, italic, inline code, code blocks, tables, lists, links, and horizontal rules.
 */
function markdownToHtml(md: string): string {
  let html = md

  // Fenced code blocks (``` ... ```) are pulled out into placeholders before
  // the line-by-line pass below, since their contents span multiple lines
  // and must be preserved verbatim rather than being treated as wrapped
  // paragraph text (and merged/reflowed) or misinterpreted as other block
  // elements. The placeholders are swapped back for the real HTML at the end.
  const codeBlocks: string[] = []
  html = html.replace(/```(\w*)\n([\s\S]*?)```/g, (_match, _lang, code) => {
    const placeholder = `<!--CODEBLOCK_${codeBlocks.length}-->`
    codeBlocks.push(
      `<pre><code>${code.replace(/</g, '&lt;').replace(/>/g, '&gt;').trimEnd()}</code></pre>`
    )
    return placeholder
  })

  // Tables
  html = html.replace(/((?:^\|.+\|$\n?)+)/gm, (tableBlock) => {
    const rows = tableBlock.trim().split('\n')
    if (rows.length < 2) return tableBlock
    const headerCells = rows[0]
      .split('|')
      .filter((c) => c.trim() !== '')
      .map((c) => `<th>${c.trim()}</th>`)
      .join('')
    const bodyRows = rows
      .slice(2)
      .map((row) => {
        const cells = row
          .split('|')
          .filter((c) => c.trim() !== '')
          .map((c) => `<td>${c.trim()}</td>`)
          .join('')
        return `<tr>${cells}</tr>`
      })
      .join('')
    return `<table><thead><tr>${headerCells}</tr></thead><tbody>${bodyRows}</tbody></table>`
  })

  // Process line by line for block elements
  const lines = html.split('\n')
  const processed: string[] = []
  let inList = false
  let listType = ''

  // Markdown paragraphs (and list items) are conventionally soft-wrapped
  // across multiple source lines; those lines need to be joined into a
  // single reflowing block rather than one per line, or the rendered text
  // will wrap at the source file's line length instead of the container's
  // width. A line ending in two-or-more trailing spaces is a markdown hard
  // break, which is preserved as an explicit <br> rather than being joined
  // with a space.
  const joinWrappedLines = (buffer: { text: string; hardBreak: boolean }[]) =>
    buffer
      .map((line, idx) => {
        if (idx === buffer.length - 1) return line.text
        return line.hardBreak ? `${line.text}<br>` : `${line.text} `
      })
      .join('')

  let paragraphBuffer: { text: string; hardBreak: boolean }[] = []
  const flushParagraph = () => {
    if (paragraphBuffer.length) {
      processed.push(`<p>${joinWrappedLines(paragraphBuffer)}</p>`)
      paragraphBuffer = []
    }
  }

  // Buffers the current list item's text so continuation lines (wrapped,
  // indented lines that follow a "- " or "1. " marker) are joined into the
  // same <li> instead of spilling out into their own paragraph.
  let listItemBuffer: { text: string; hardBreak: boolean }[] = []
  const flushListItem = () => {
    if (listItemBuffer.length) {
      processed.push(`<li>${joinWrappedLines(listItemBuffer)}</li>`)
      listItemBuffer = []
    }
  }

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]

    // Headings
    const headingMatch = line.match(/^(#{1,6})\s+(.+)$/)
    if (headingMatch) {
      flushParagraph()
      if (inList) {
        flushListItem()
        processed.push(listType === 'ol' ? '</ol>' : '</ul>')
        inList = false
      }
      const level = headingMatch[1].length
      processed.push(`<h${level}>${headingMatch[2]}</h${level}>`)
      continue
    }

    // Horizontal rule
    if (/^---+$/.test(line.trim())) {
      flushParagraph()
      if (inList) {
        flushListItem()
        processed.push(listType === 'ol' ? '</ol>' : '</ul>')
        inList = false
      }
      processed.push('<hr />')
      continue
    }

    // Ordered list
    const olMatch = line.match(/^(\d+)\.\s+(.+)$/)
    if (olMatch) {
      flushParagraph()
      flushListItem()
      if (!inList || listType !== 'ol') {
        if (inList) processed.push(listType === 'ol' ? '</ol>' : '</ul>')
        processed.push('<ol>')
        inList = true
        listType = 'ol'
      }
      listItemBuffer.push({ text: olMatch[2], hardBreak: /  +$/.test(line) })
      continue
    }

    // Unordered list
    const ulMatch = line.match(/^[-*]\s+(.+)$/)
    if (ulMatch) {
      flushParagraph()
      flushListItem()
      if (!inList || listType !== 'ul') {
        if (inList) processed.push(listType === 'ol' ? '</ol>' : '</ul>')
        processed.push('<ul>')
        inList = true
        listType = 'ul'
      }
      listItemBuffer.push({ text: ulMatch[1], hardBreak: /  +$/.test(line) })
      continue
    }

    // Blank lines
    if (line.trim() === '') {
      flushParagraph()
      if (inList) {
        flushListItem()
        processed.push(listType === 'ol' ? '</ol>' : '</ul>')
        inList = false
      }
      processed.push('')
      continue
    }

    // Skip lines that are already HTML (tables, pre)
    if (line.startsWith('<')) {
      flushParagraph()
      if (inList) {
        flushListItem()
        processed.push(listType === 'ol' ? '</ol>' : '</ul>')
        inList = false
      }
      processed.push(line)
      continue
    }

    // A non-blank line that isn't a heading/rule/new list item: if we're
    // in the middle of a list item, it's a wrapped continuation of that
    // item's text; otherwise it's a wrapped paragraph line. Both are
    // buffered until a blank line or another block element ends them.
    if (inList && listItemBuffer.length) {
      listItemBuffer.push({ text: line.trim(), hardBreak: /  +$/.test(line) })
    } else {
      paragraphBuffer.push({ text: line.trim(), hardBreak: /  +$/.test(line) })
    }
  }

  flushParagraph()
  if (inList) {
    flushListItem()
    processed.push(listType === 'ol' ? '</ol>' : '</ul>')
  }

  html = processed.join('\n')

  // Inline formatting (applied after block structure)
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
  html = html.replace(/\*(.+?)\*/g, '<em>$1</em>')
  html = html.replace(/`([^`]+)`/g, '<code>$1</code>')
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>')

  // Swap the fenced code block placeholders back in for their real HTML.
  html = html.replace(
    /<!--CODEBLOCK_(\d+)-->/g,
    (_match, index) => codeBlocks[Number(index)]
  )

  return html
}

export function MarkdownPage({
  getBySlug,
  backPath,
  backLabel,
}: MarkdownPageProps) {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()

  const entry = slug ? getBySlug(slug) : undefined

  if (!entry) {
    return (
      <Box>
        <Button
          variant="subtle"
          color="gray"
          leftSection={<IconArrowLeft size={16} />}
          onClick={() => navigate(backPath)}
          mb="lg"
        >
          {backLabel}
        </Button>
        <Card shadow="sm" withBorder p="xl" ta="center">
          <Text size="lg" fw={600} c="gray.9" mb="sm">
            Page Not Found
          </Text>
          <Text size="sm" c="gray.6">
            The requested document could not be found.
          </Text>
        </Card>
      </Box>
    )
  }

  return (
    <Box>
      <Button
        variant="subtle"
        color="gray"
        leftSection={<IconArrowLeft size={16} />}
        onClick={() => navigate(backPath)}
        mb="lg"
      >
        {backLabel}
      </Button>
      <Card shadow="sm" withBorder>
        <Box p="xl">
          <TypographyStylesProvider>
            <div
              dangerouslySetInnerHTML={{
                __html: markdownToHtml(entry.content),
              }}
            />
          </TypographyStylesProvider>
        </Box>
      </Card>
    </Box>
  )
}
